import { VSBuffer } from 'vscode/vscode/vs/base/common/buffer';
import { joinPath } from 'vscode/vscode/vs/base/common/resources';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { registerAction2, Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IFileDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { CHAT_CATEGORY } from 'vscode/vscode/vs/workbench/contrib/chat/browser/actions/chatActions';
import { IChatWidgetService } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat.service';
import { ChatEditorInput } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chatEditorInput';
import { CONTEXT_CHAT_ENABLED } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatContextKeys';
import { isExportableSessionData } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatModel';
import { IChatService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService.service';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

const _moduleId = "vs/workbench/contrib/chat/browser/actions/chatImportExport";
const defaultFileName = 'chat.json';
const filters = [{ name: ( localizeWithPath(_moduleId, 0, "Chat Session")), extensions: ['json'] }];
function registerChatExportActions() {
    registerAction2(class ExportChatAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.export',
                category: CHAT_CATEGORY,
                title: ( localize2WithPath(_moduleId, 1, "Export Chat...")),
                precondition: CONTEXT_CHAT_ENABLED,
                f1: true,
            });
        }
        async run(accessor, ...args) {
            const widgetService = accessor.get(IChatWidgetService);
            const fileDialogService = accessor.get(IFileDialogService);
            const fileService = accessor.get(IFileService);
            const chatService = accessor.get(IChatService);
            const widget = widgetService.lastFocusedWidget;
            if (!widget || !widget.viewModel) {
                return;
            }
            const defaultUri = joinPath(await fileDialogService.defaultFilePath(), defaultFileName);
            const result = await fileDialogService.showSaveDialog({
                defaultUri,
                filters
            });
            if (!result) {
                return;
            }
            const model = chatService.getSession(widget.viewModel.sessionId);
            if (!model) {
                return;
            }
            const content = VSBuffer.fromString(JSON.stringify(model.toExport(), undefined, 2));
            await fileService.writeFile(result, content);
        }
    });
    registerAction2(class ImportChatAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.import',
                title: ( localize2WithPath(_moduleId, 2, "Import Chat...")),
                category: CHAT_CATEGORY,
                precondition: CONTEXT_CHAT_ENABLED,
                f1: true,
            });
        }
        async run(accessor, ...args) {
            const fileDialogService = accessor.get(IFileDialogService);
            const fileService = accessor.get(IFileService);
            const editorService = accessor.get(IEditorService);
            const defaultUri = joinPath(await fileDialogService.defaultFilePath(), defaultFileName);
            const result = await fileDialogService.showOpenDialog({
                defaultUri,
                canSelectFiles: true,
                filters
            });
            if (!result) {
                return;
            }
            const content = await fileService.readFile(result[0]);
            try {
                const data = JSON.parse(( (content.value.toString())));
                if (!isExportableSessionData(data)) {
                    throw ( (new Error('Invalid chat session data')));
                }
                await editorService.openEditor({ resource: ChatEditorInput.getNewEditorUri(), options: { target: { data }, pinned: true } });
            }
            catch (err) {
                throw err;
            }
        }
    });
}

export { registerChatExportActions };
